import first from 'lodash/first'
import noop from 'lodash/noop'
import React, { FC, RefCallback, useCallback, useContext, useEffect, useState } from 'react'

type GroupVisibility = Record<string, boolean>

interface ScrollPosition {
  groupVisibility: GroupVisibility
  updateGroupVisibility: (group: string, isVisible: boolean) => void
}

export const ScrollPositionContext = React.createContext<ScrollPosition>({
  groupVisibility: {},
  updateGroupVisibility: noop,
})

export const ScrollPositionProvider: FC = ({ children }) => {
  const [groupVisibility, setGroupVisibility] = useState<GroupVisibility>({})
  const updateGroupVisibility = useCallback((group, isVisible) => {
    setGroupVisibility(prev => ({
      ...prev,
      [group]: isVisible,
    }))
  }, [])

  return (
    <ScrollPositionContext.Provider
      // TODO: avoid rerendering when no visibility changes
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        groupVisibility,
        updateGroupVisibility,
      }}
    >
      {children}
    </ScrollPositionContext.Provider>
  )
}

export const useGroupVisibility = (group: string): boolean | undefined => {
  const { groupVisibility } = useContext(ScrollPositionContext)
  return groupVisibility[group]
}

export const useGroupVisibilityUpdater = (group: string): RefCallback<Element> => {
  const { updateGroupVisibility } = useContext(ScrollPositionContext)
  const [observer, setObserver] = useState<IntersectionObserver>()

  // Create a new observer when dependencies change.
  useEffect(() => {
    const callback: IntersectionObserverCallback = (entries) => {
      updateGroupVisibility(group, first(entries)?.isIntersecting || false)
    }
    if (typeof IntersectionObserver === 'undefined') {
      return
    }
    setObserver(new IntersectionObserver(callback))
  }, [group, updateGroupVisibility])

  // Create the cleanup function when observer changes.
  useEffect(() => () => {
    if (observer) {
      updateGroupVisibility(group, false)
      observer.disconnect()
    }
  }, [group, observer, updateGroupVisibility])

  // Target the observer when the watched ref changes.
  return useCallback<RefCallback<Element>>((element) => {
    if (element && observer) {
      observer.observe(element)
    }
  }, [observer])
}
